import { UTCDate } from '@date-fns/utc';
import { formatISO } from 'date-fns';
import startOfMonth from 'date-fns/startOfMonth';
import mixpanel from 'mixpanel-browser';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { COLUMN_TYPE_TO_MIXPANEL_TYPE_MAPPING, FILTERS_DATA, KEY_MAP } from '~/containers/Sims/constants';
import useAuthentication from '~/hooks/use-authentication';
import useDebounce from '~/hooks/use-debounce';
import useSearchSimsFilters from '~/hooks/use-search-sims-filters';
import useTiering from '~/hooks/use-tiering';
import api, { useConfig } from '~/lib/api';
import { fromApi, toApi } from '~/lib/data-parsers/sim';
import { transformToUtc } from '~/lib/dates';
import { bytesToSize, numberToEUR } from '~/lib/numbers';
import useNotification from './use-notification';
import usePagination from './use-pagination';

const DEFAULT_DOWNLOAD_ERROR_MESSAGE =
  'Something went wrong while downloading the sim report file. Try again later or contact support if the issue persists.';

const CSV_COLUMN_LABELS = {
  id: 'ID',
  label: 'Label',
  iccid: 'ICCID',
  imsi: 'IMSI',
  type: 'Type',
  activated: 'Activated',
  lastCameOnlineAt: 'Last Came Online At',
  ipv4: 'IPv4',
  usage: 'Usage',
  connector: 'Connector',
  networkWhitelist: 'Network Whitelist',
  tags: 'Tags',
  technologies: 'Technologies',
  networkName: 'Network Name',
  networkCountryCode: 'Network Country Code',
  networkMcc: 'Network MCC',
  networkMnc: 'Network MNC',
  costAlertPeriod: 'Cost Alert Period',
  costAlertThreshold: 'Cost Alert Threshold',
  costAlertResetDate: 'Cost Alert Reset Date',
  dataLimitPeriod: 'Data Limit Period',
  dataLimitAmount: 'Data Limit Amount',
  dataLimitThreshold: 'Data Limit Threshold',
  activationCode: 'Activation Code',
};

function formatTechnologies(technologies) {
  return [
    technologies?.sms ? 'SMS' : null,
    technologies?.['2g_3g'] ? '2G/3G' : null,
    technologies?.['4g'] ? '4G' : null,
  ]
    .filter(Boolean)
    .join(', ');
}

function flattenForCsv(sim) {
  return {
    // ="..." formula so Excel/Sheets preserve leading zeros by treating the value as text
    id: `="${sim.id}"`,
    label: sim.label,
    iccid: sim.iccid,
    imsi: sim.imsi,
    type: sim.type,
    activated: sim.activated,
    lastCameOnlineAt: sim.lastCameOnlineAt,
    ipv4: sim.ipv4,
    usage: sim.usage != null ? bytesToSize(sim.usage) : '',
    connector: sim.connector,
    networkWhitelist: sim.networkWhitelist,
    tags: sim.tags?.map(t => t.name).join(', ') ?? '',
    technologies: formatTechnologies(sim.technologies),
    networkName: sim.network?.name ?? '',
    networkCountryCode: sim.network?.countryCode ?? '',
    networkMcc: sim.network?.mcc ?? '',
    networkMnc: sim.network?.mnc ?? '',
    costAlertPeriod: sim.costAlert?.period ?? '',
    costAlertThreshold: sim.costAlert?.threshold != null ? numberToEUR(sim.costAlert.threshold) : '',
    costAlertResetDate: sim.costAlert?.resetsAt ?? '',
    dataLimitPeriod: sim.dataLimit?.type ?? '',
    dataLimitAmount: sim.dataLimit?.total != null ? bytesToSize(sim.dataLimit.total) : '',
    dataLimitThreshold: sim.dataLimit?.alertThreshold != null ? bytesToSize(sim.dataLimit.alertThreshold) : '',
    activationCode: sim.activationCode ?? '',
  };
}

function toCsv(rows) {
  if (!rows.length) return '';
  // Keys of the first row become headers
  const headers = Object.keys(rows[0]);
  // Values containing commas, quotes, or newlines are wrapped in double quotes with inner quotes doubled
  const escapeCell = value => {
    const str = String(value ?? '');
    return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const headerRow = headers.map(escapeCell).join(',');
  const dataRows = rows.map(row => headers.map(header => escapeCell(row[header])).join(','));
  return [headerRow, ...dataRows].join('\n');
}

export const CSV_FILTER_KEYS = Object.entries(FILTERS_DATA)
  .filter(([_, column]) => column.type === 'csv')
  .map(([key]) => key);

function convertConnectorsFromApi(connectors) {
  if (!connectors || !Array.isArray(connectors)) return [];
  return connectors.map(connector => {
    return {
      id: connector.id,
      name: connector.name,
      type: connector.type,
      hasSims: connector.has_sims,
      hasAllQueriedSims: connector.has_all_queried_sims,
    };
  });
}

function convertNetworkWhitelistsFromApi(networkWhitelists) {
  if (!networkWhitelists || typeof networkWhitelists !== 'object') return {};
  const converted = {};
  Object.entries(networkWhitelists).forEach(([tech, data]) => {
    converted[tech] = {
      id: data.id,
      name: data.name,
      hasSims: data.has_sims,
      hasAllQueriedSims: data.has_all_queried_sims,
    };
  });
  return converted;
}

function convertTagsFromApi(tags) {
  if (!tags || !Array.isArray(tags)) return [];
  return tags.map(tag => {
    return {
      id: tag.id,
      name: tag.name,
      canWrite: tag.can_write,
      color: tag.color,
      hasSims: tag.has_sims,
      hasAllQueriedSims: tag.has_all_queried_sims,
      simIds: tag.sim_ids,
    };
  });
}

function convertTechnologiesFromApi(technologies) {
  if (!technologies || typeof technologies !== 'object') return {};
  const converted = {};
  Object.entries(technologies).forEach(([tech, data]) => {
    converted[tech] = {
      hasSims: data.has_sims,
      hasAllQueriedSims: data.has_all_queried_sims,
    };
  });
  return converted;
}

function getInitialFilters(sortParam) {
  return sortParam.map(item => {
    const [key, direction] = item.split(':');
    return { key, direction };
  });
}

function useSims({
  organizationId,
  disableFetchSimsOnChange = false,
  disableSetUrlParamsOnSearch = false,
  ignoreQueryParams = false,
  from,
}) {
  const location = useLocation();
  const pagination = usePagination();
  const { addNotification } = useNotification();

  const queryString = new URLSearchParams(ignoreQueryParams ? '' : location.search);
  const searchParam = queryString.get('search') || '';
  const filterParam = queryString.getAll('filter');
  const sortParam = queryString.getAll('sort');

  function fromApiFilter(key, value) {
    const newKey = key.replace('network_whitelist', 'networkWhitelist');
    const newValue = value.replace('none', 'None');
    return { key: newKey, value: newValue };
  }

  let initialFilter = [];
  if (filterParam) {
    const newFilterArray = [];
    filterParam.map(filter => {
      const [key, value] = filter.split(':');
      return newFilterArray.push(fromApiFilter(key, value));
    });
    initialFilter = newFilterArray;
  }

  const history = useHistory();
  const { isLoggedIn, assumedOrganizationId, isOrgMember } = useAuthentication();
  const { hasFeature } = useTiering();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(searchParam);
  const [searchLoading, setSearchLoading] = useState(false);
  const [order, setOrder] = useState(sortParam ? getInitialFilters(sortParam) : []);
  const debouncedSearch = useDebounce(search);
  const createConfig = useConfig(organizationId || assumedOrganizationId);
  const filters = useSearchSimsFilters(
    organizationId || assumedOrganizationId,
    initialFilter && initialFilter.length > 0 ? initialFilter : null,
    { resetPages },
  );
  const [filter, setFilter] = useState(initialFilter);
  const [affectAll, setAffectAll] = useState(false);
  const [selectedSims, setSelectedSims] = useState([]);
  const [simDownloadLoading, setSimDownloadLoading] = useState(false);

  function resetPages() {
    pagination.setPage(1);
  }

  function toApiSort(order) {
    return order.map(item => {
      if (KEY_MAP[item.key]) return `${KEY_MAP[item.key]}:${item.direction}`;
      return `${item.key}:${item.direction}`;
    });
  }

  function toApiFilter(key, value) {
    const newKey = FILTERS_DATA[key]?.toApiKey ?? key;
    const newValue = value.replace('None', 'none');
    return `${newKey}:${newValue}`;
  }

  function convertFilter(filterArray) {
    return filterArray.map(filter => toApiFilter(filter.key, filter.value));
  }

  function getPathName(search, filter, order) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filter?.length > 0) {
      const nonCSVFilters = filter.filter(f => !CSV_FILTER_KEYS.includes(f.key));
      nonCSVFilters.forEach(filter => {
        params.append('filter', `${filter.key}:${filter.value}`);
      });
    }
    if (order?.length > 0) {
      order.forEach(item => {
        params.append('sort', `${item.key}:${item.direction}`);
      });
    }
    if (pagination.currentPage !== 1) params.set('page', pagination.currentPage);
    if (pagination.currentPage === 1) params.delete('page');
    if (pagination.itemsPerPage !== 20) params.set('limit', pagination.itemsPerPage);
    if (pagination.itemsPerPage === 20) params.delete('limit');
    return `${location.pathname}?${params.toString()}`;
  }
  async function getCount(additionalFilters) {
    const config = {
      params: {
        filter: convertFilter(filter.concat(additionalFilters || [])),
        search: search?.trim().length > 0 ? search : undefined,
      },
    };
    const hasCSVFilter = filter.concat(additionalFilters || []).some(f => CSV_FILTER_KEYS.includes(f.key));
    const method = hasCSVFilter ? 'post' : 'get';
    const response = await api.request(
      createConfig({
        method,
        url: '/sims/count',
        ...(method === 'get' ? { params: config.params } : { data: config.params }),
      }),
    );
    return response;
  }

  useEffect(() => {
    if (!isLoggedIn) return;
    async function count() {
      pagination.setLoadingTotalItems(true);
      const response = await getCount();
      pagination.setTotalItems(response.data.total);
      pagination.setLoadingTotalItems(false);
      if (search || filter?.length) {
        const filtersUsed = filter?.map(f => {
          const filterData = FILTERS_DATA[f.key];
          const type =
            filterData?.mixpanelType ||
            COLUMN_TYPE_TO_MIXPANEL_TYPE_MAPPING[filterData?.type] ||
            filterData?.type ||
            'unknown';
          return `${type}:${f.key}`;
        });
        const distinctFiltersUsed = [...new Set(filtersUsed)];
        mixpanel.track('Search sims', {
          'Search query': search,
          From: from,
          'Filters used': distinctFiltersUsed,
          '# Total search results': response.data.total,
        });
      }
    }

    count();
  }, [isLoggedIn, debouncedSearch, JSON.stringify(filter), assumedOrganizationId]);

  function clearSelection() {
    setAffectAll(false);
    setSelectedSims([]);
    setData(prev => prev?.map(s => ({ ...s, checked: false })));
  }
  const abortControllerRef = useRef(null);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const downloadAsCsv = useCallback(
    async ({ columns, columnsInTableOrder = [], mandatoryColumnKeys = [] }) => {
      const params = {
        start: transformToUtc(startOfMonth(new Date())),
        end: transformToUtc(new Date()),
        limit: affectAll ? pagination.totalItems : selectedSims.length,
      };
      if (!affectAll) {
        params.filter = selectedSims.map(s => `sim_id:${s.id}`);
      } else {
        const convertedFilter = convertFilter(filter);
        if (convertedFilter?.length) params.filter = convertedFilter;
        if (search) params.search = search;
      }
      setSimDownloadLoading(true);
      try {
        const response = await api.request(
          createConfig({
            method: 'post',
            url: '/sims/csv',
            data: params,
          }),
        );
        const tableOrderedColumnKeys = columnsInTableOrder.map(column => column.key);
        const hasEsim = response.data.some(sim => fromApi(sim, isOrgMember).type === 'eSIM');

        const exportableMandatoryKeys = mandatoryColumnKeys.filter(key => key in CSV_COLUMN_LABELS);
        const selectAllColumns = columns === null;
        const selectedColumns = columns && new Set(columns.map(column => column.value ?? column.key));
        const activationCodeColumn = hasEsim ? ['activationCode'] : [];
        const orderedColumns = selectAllColumns
          ? [...exportableMandatoryKeys, ...tableOrderedColumnKeys, ...activationCodeColumn]
          : [
              ...exportableMandatoryKeys,
              ...tableOrderedColumnKeys.filter(
                column => !exportableMandatoryKeys.includes(column) && selectedColumns.has(column),
              ),
              ...activationCodeColumn,
            ];

        const csvRows = response.data.map(sim => {
          const flatSim = flattenForCsv(fromApi(sim, isOrgMember));
          return Object.fromEntries(
            orderedColumns.map(column => [CSV_COLUMN_LABELS[column] ?? column, flatSim[column] ?? '']),
          );
        });

        const blob = new Blob([toCsv(csvRows)], { type: 'text/csv' });
        const link = document.createElement('a');
        const currentDate = formatISO(new UTCDate(), { format: 'basic' });
        link.href = window.URL.createObjectURL(blob);
        link.download = `sims-${currentDate}.csv`;
        link.click();
        mixpanel.track('Download SIMs table as CSV', {
          'CSV export columns list': Object.keys(csvRows[0] ?? {}),
          'CSV export row count': csvRows.length,
          'Export all available data': selectAllColumns,
        });
        setSimDownloadLoading(false);
      } catch (error) {
        addNotification({
          type: 'error',
          message: error.response?.data?.details?.message || DEFAULT_DOWNLOAD_ERROR_MESSAGE,
          expire: 10000,
        });
        setSimDownloadLoading(false);
      }
    },
    [addNotification, createConfig, affectAll, selectedSims, filter, search, pagination, isOrgMember],
  );

  async function searchSims() {
    if (!isLoggedIn) return;
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const config = {
        params: {
          limit: pagination.itemsPerPage,
          offset: pagination.getOffset(),
          start: transformToUtc(startOfMonth(new Date())),
          end: transformToUtc(new Date()),
        },
        signal: abortControllerRef.current.signal,
      };
      if (search) {
        config.params.search = search;
      }
      let method = 'get';
      if (filter) {
        config.params.filter = convertFilter(filter);
        if (CSV_FILTER_KEYS.some(key => filter.find(f => f.key === key))) {
          method = 'post';
        }
      }
      if (order) {
        config.params.sort = toApiSort(order);
      }
      const response = await api.request(
        createConfig({
          method,
          url: '/sims/find',
          signal: config.signal,
          ...(method === 'get' ? { params: config.params } : { data: config.params }),
        }),
      );
      if (!disableSetUrlParamsOnSearch) {
        history.replace(getPathName(search, filter, order));
      }
      setData(response.data.sims.map(s => fromApi(s, isOrgMember)));
      pagination.setIsPaging(false);
      setSearchLoading(false);
      setLoading(false);
    } catch (e) {
      if (e.name === 'CanceledError' || e.code === 'ERR_CANCELED') {
        console.info('Request canceled, ignoring');
        return;
      }
      setError(e);
      pagination.setIsPaging(false);
      setSearchLoading(false);
      setLoading(false);
    }
  }

  async function getAttributes(simIds = []) {
    const config = { params: {} };
    if (search) config.params.search = search;
    const payload = {};
    if (filter) {
      payload.filter = convertFilter(filter);
    }

    if (simIds.length > 0) {
      payload.filter = payload.filter || [];
      payload.filter.push(...simIds.map(id => `sim_id:${id}`));
    }

    const { data, loading } = await api.request(
      createConfig({
        method: 'post',
        url: '/sims/bulk-apply/attributes',
        params: config.params,
        data: payload,
      }),
    );
    const { tags = [], technologies = {}, labels = [], connectors = [], networkWhitelists = {} } = data;

    return {
      tags: convertTagsFromApi(tags),
      technologies: convertTechnologiesFromApi(technologies),
      labels,
      connectors: convertConnectorsFromApi(connectors),
      networkWhitelists: convertNetworkWhitelistsFromApi(networkWhitelists),
      loading,
    };
  }

  async function getDetails(sim) {
    const response = await api.get(`/sims/${sim.id}`, createConfig());
    setData(
      data.map(s => {
        if (s.id !== sim.id) return s;
        return fromApi(response.data, isOrgMember);
      }),
    );
    return fromApi(response.data, isOrgMember);
  }

  async function create(sim) {
    const formattedTechFee = !sim.techFee ? null : sim.techFee;

    const payload = {
      id: sim.id,
      apn_profile: sim.apnProfile,
      tech_fee: formattedTechFee,
    };
    const response = await api.post('/sims', payload, createConfig());
    setData([...data, fromApi(response.data, isOrgMember)].sort((a, b) => Number(a.id) - Number(b.id)));
  }

  async function update(sim, trackRequest = true) {
    const response = await api.patch(
      `/sims/${sim.id}`,
      toApi(sim, isOrgMember, hasFeature),
      createConfig({
        params: {
          track: trackRequest,
        },
      }),
    );
    const updatedSim = fromApi(response.data, isOrgMember);
    setData(prevData => prevData?.map(r => (r.id === sim.id ? updatedSim : r)));
  }

  async function bulkAddTags({ addTags, removeTags }) {
    const config = createConfig({ params: { search } });
    const payload = {
      add: addTags.map(t => t.value),
      remove: removeTags.map(t => t.value),
      sims: selectedSims.map(s => s.id),
    };
    const response = await api.patch('/sims/set-delta', payload, config);
    return response.data;
  }

  async function bulkAction({ affectAll, ...values }) {
    const options = {
      params: {},
    };
    const payload = {};
    if (search) options.params.search = search;
    const config = createConfig(options);

    if (filter) payload.filter = convertFilter(filter);
    if (!affectAll && selectedSims.length > 0) {
      payload.filter.push(...selectedSims.map(s => `sim_id:${s.id}`));
    }

    const {
      setLabel,
      dataLimit,
      costAlert,
      setConnector,
      addTags,
      removeTags,
      setNetworkWhitelist,
      addTechnologies,
      removeTechnologies,
    } = values;
    const operations = [];

    // Tags
    if (addTags?.length)
      operations.push({
        op: 'add',
        path: '/tags',
        value: addTags.map(t => t.value || t.id),
      });

    if (removeTags?.length)
      operations.push({
        op: 'remove',
        path: '/tags',
        value: removeTags.map(t => t.value || t.id),
      });

    // Connector
    if (setConnector !== undefined)
      operations.push({
        op: 'set',
        path: '/connector',
        value: setConnector,
      });

    // Network list
    if (setNetworkWhitelist !== undefined)
      operations.push({
        op: 'set',
        path: '/network_whitelist',
        value: setNetworkWhitelist,
      });

    if (addTechnologies?.length)
      operations.push({
        op: 'add',
        path: '/technologies',
        value: addTechnologies,
      });

    if (removeTechnologies?.length)
      operations.push({
        op: 'remove',
        path: '/technologies',
        value: removeTechnologies,
      });
    // Label
    if (setLabel !== undefined)
      operations.push({
        op: 'set',
        path: '/label',
        value: setLabel,
      });

    // Data limit
    if (dataLimit !== undefined)
      operations.push({
        op: 'set',
        path: '/data_limit',
        value: dataLimit,
      });
    // Cost alert
    if (costAlert !== undefined)
      operations.push({
        op: 'set',
        path: '/cost_alert',
        value: costAlert,
      });

    payload.ops = operations;

    const response = await api.patch('/sims/bulk-apply', payload, config);
    return response.data;
  }

  useEffect(() => {
    setSearchLoading(search?.length > 0);
  }, [search]);

  useEffect(() => {
    if (search?.length >= 1) {
      setLoading(true);
      setSearchLoading(true);
      pagination.setIsPaging(true);
      searchSims();
      return;
    }
    if (disableFetchSimsOnChange) return;
    setLoading(true);
    setSearchLoading(true);
    pagination.setIsPaging(true);
    searchSims();
  }, [
    isLoggedIn,
    debouncedSearch,
    JSON.stringify(filter),
    assumedOrganizationId,
    order,
    pagination.currentPage,
    pagination.itemsPerPage,
  ]);

  useEffect(() => {
    if (!data) return;
    if (affectAll) {
      if (data.every(s => s.checked)) return;
      setData(prev => prev?.map(s => (s.checked ? s : { ...s, checked: true })));
      return;
    }

    const selectedIds = new Set(selectedSims.map(s => s.id));
    const needsUpdate = data.some(s => s.checked !== selectedIds.has(s.id));
    if (!needsUpdate) return;

    setData(prev =>
      prev?.map(s => {
        const shouldCheck = selectedIds.has(s.id);
        return s.checked === shouldCheck ? s : { ...s, checked: shouldCheck };
      }),
    );
  }, [affectAll, data, selectedSims]);

  useEffect(() => {
    clearSelection();
  }, [debouncedSearch, filter]);

  return {
    loading,
    error,
    data,
    create,
    update,
    getDetails,
    bulkEdit: {
      getAttributes,
      toggleItem(sim) {
        if (affectAll) return;
        setData(prev => prev.map(s => (sim.id === s.id ? { ...s, checked: !sim.checked } : s)));
        setSelectedSims(prev => {
          const selectedIndex = prev.findIndex(s => s.id === sim.id);
          const exists = selectedIndex !== -1;
          if (!exists && !sim.checked) return [...prev, sim];
          else if (exists && sim.checked) return prev.filter(s => s.id !== sim.id);
          return prev;
        });
      },
      toggleAll(checked) {
        setData(prev => {
          const sims = prev.map(s => ({ ...s, checked }));
          const selected = [...selectedSims];
          sims.forEach(s => {
            const index = selected.findIndex(ss => ss.id === s.id);
            const exists = index !== -1;
            if (!exists && checked) selected.push(s);
            else if (exists && !checked) selected.splice(index, 1);
          });
          setSelectedSims(selected);
          return sims;
        });
      },
      clearSelection,
      bulkAction,
      bulkAddTags,
      setAffectAll,
      affectAll,
    },
    pagination: {
      ...pagination,
      goToNextPage: () => {
        mixpanel.track('Paginate sims forwards', {
          'Page offset': pagination.getOffset(),
        });
        pagination.goToNextPage();
      },
      goToPreviousPage: () => {
        mixpanel.track('Paginate sims backwards', {
          'Page offset': pagination.getOffset(),
        });
        pagination.goToPreviousPage();
      },
      setItemsPerPage: limit => {
        mixpanel.track('Paginate set sims per page', {
          'Items per page': limit,
        });
        pagination.setItemsPerPage(limit || 20);
      },
    },
    search: {
      value: search,
      onChange(e) {
        resetPages();
        setSearch(e.target.value);
      },
      clear() {
        setSearch('');
        setData(null);
      },
      reset() {
        setSearch('');
        filters.clear();
        setOrder(null);
        resetPages();
      },
      loading: searchLoading,
      placeholder: 'Search by ID, label, tag...',
      width: '220px',
      maxLength: 1000,
    },
    getCount,
    filters: {
      ...filters,
      clear() {
        setSearch('');
        filters.clear();
      },
      apply() {
        setFilter(filters.activeFilters);
      },
    },
    order: {
      order,
      orderChange: setOrder,
    },
    searchSims,
    selected: selectedSims,
    setSelected: setSelectedSims,
    downloadAsCsv,
    simDownloadLoading,
  };
}

export default useSims;
